import React, { lazy, Suspense, useEffect, useState } from "react";
import "./index.css";
import BrandLoader from "./components/common/BrandLoader";
import { ThemeProvider } from "./contexts/ThemeContext";

const AdminApp = lazy(() => import("./AdminApp"));
const ClientApp = lazy(() => import("./portals/client/ClientApp"));
const EmployeeApp = lazy(() => import("./portals/employee/EmployeeApp"));
const UnifiedLoginPage = lazy(() => import("./pages/UnifiedLoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

const portals = {
  admin: AdminApp,
  client: ClientApp,
  employee: EmployeeApp,
};

// Which portal renders is decided by who's actually logged in, not by
// subdomain: UnifiedLoginPage figures out the role from the credentials
// (via /api/auth/login) and stores which portal to open under
// "activePortal" before reloading. If nobody's logged in, everyone lands
// on the same login screen.
const getActivePortal = () => {
  const portal = localStorage.getItem("activePortal");
  if (portal === "admin" && localStorage.getItem("token")) return "admin";
  if (portal === "employee" && localStorage.getItem("employeeUser")) return "employee";
  if (portal === "client" && localStorage.getItem("clientUser")) return "client";
  return null;
};

// Standalone, publicly reachable pages that bypass the portal switch above
// (reachable even while logged out, or from an emailed/shown reset link).
const PUBLIC_PATH_PAGES = {
  "/forgot-password": ForgotPasswordPage,
  "/reset-password": ResetPasswordPage,
};

function App() {
  const [portal] = useState(getActivePortal);
  const publicPage = PUBLIC_PATH_PAGES[window.location.pathname];
  const Portal = publicPage || (portal ? portals[portal] : UnifiedLoginPage);

  /* Hand off from the static index.html splash to React once we've mounted */
  useEffect(() => {
    const splash = document.getElementById("app-splash");
    if (splash) {
      splash.style.opacity = "0";
      setTimeout(() => splash.remove(), 350);
    }
  }, []);

  return (
    <ThemeProvider>
      <Suspense fallback={<BrandLoader fullScreen />}>
        <Portal />
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
